/**
 * Helpers for the "Open Last 7 Days" bulk-open action on the /entities screen.
 *
 * Two concerns live here, both testable without a browser:
 *  1. The date window sent to the entities API — see `getRecentDateRange`.
 *  2. Which entity URLs are safe to hand to `window.open` — see
 *     `toOpenablePostUrl`. Entity URLs are scraped data, so only http(s) links
 *     are ever opened.
 */

/** How far back "the last 7 days" reaches, today included. */
export const RECENT_POSTS_DAYS = 7;

/** Local YYYY-MM-DD, the format the entity filters take (see convertToDateRangeFilter). */
const toLocalDateString = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The last `days` calendar days, today included: 7 days means today plus the
 * six days before it. Whole days, because the filter widens `fromdate` to the
 * start of its day and `todate` to the end of its own.
 * @param {number} days - Length of the window in days
 * @param {Date} now - Reference date, injectable for tests
 * @returns {{fromdate: string, todate: string}} Dates in YYYY-MM-DD
 */
export const getRecentDateRange = (days = RECENT_POSTS_DAYS, now = new Date()) => {
  const span = Math.max(1, Math.floor(days));
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (span - 1));
  return {
    fromdate: toLocalDateString(from),
    todate: toLocalDateString(now),
  };
};

/**
 * Normalize an entity URL for opening in a new tab.
 * @param {string} value - Entity `Url` as stored, i.e. untrusted scraped data
 * @returns {string|null} The parsed link, or null when it is not an explicit
 *   http(s) URL — a scheme-less or `javascript:` value never gets opened.
 */
export const toOpenablePostUrl = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || /\s/.test(trimmed)) return null;

  let url;
  try {
    url = new URL(trimmed);
  } catch (error) {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return url.href;
};
